import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useSocket } from "@/context/SocketContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { startRingtone, stopRingtone } from "@/lib/ringtone";
import { FaceMaskProcessor } from "@/lib/faceMask";

const CallContext = createContext(null);
// STUN for direct connections + free public TURN relay so calls connect
// even behind strict office/mobile NATs.
const ICE = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
  ],
};

function notify(title, body) {
  try {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification(title, { body });
    }
  } catch { /* ignore */ }
}

export function CallProvider({ children }) {
  const { addSignalListener, addPrivateListener, send } = useSocket();
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerIdRef = useRef(null);
  const pendingCandidates = useRef([]);

  // status: idle | calling | incoming | active
  const [status, setStatus] = useState("idle");
  const [peer, setPeer] = useState(null);
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [privateInfo, setPrivateInfo] = useState(null);
  // 3-way CamChat: extra peers keyed by user id, plus pending invite for this member
  const guestPcs = useRef({});
  const guestPending = useRef({});
  const [guests, setGuests] = useState({});
  const [invite, setInvite] = useState(null);
  const [consent, setConsent] = useState(null);
  const guestRoleRef = useRef(false);
  const privateRef = useRef(null);
  const answeredRef = useRef(false);
  const roleRef = useRef(null);
  const timeoutRef = useRef(null);
  // Face masking (Premium): 'off' | 'mask' | 'blur'
  const [maskMode, setMaskModeState] = useState("off");
  const maskModeRef = useRef("off");
  const processorRef = useRef(null);
  const rawStreamRef = useRef(null);

  const cleanup = useCallback(() => {
    stopRingtone();
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    // Log a "missed video date" if the caller's invite was never answered.
    if (roleRef.current === "caller" && !answeredRef.current && peerIdRef.current) {
      api.post("/calls/missed", { to_user_id: peerIdRef.current, body: "Missed video date" }).catch(() => {});
    }
    // Finalize any private session (idempotent server-side).
    if (privateRef.current?.session_id) {
      const sid = privateRef.current.session_id;
      privateRef.current = null;
      api.post("/private/end", { session_id: sid }).catch(() => {});
    }
    setPrivateInfo(null);
    roleRef.current = null;
    answeredRef.current = false;
    pcRef.current?.close();
    pcRef.current = null;
    Object.values(guestPcs.current).forEach((pc) => { try { pc.close(); } catch { /* ignore */ } });
    guestPcs.current = {}; guestPending.current = {}; setGuests({}); guestRoleRef.current = false; setConsent(null);
    processorRef.current?.stop();
    processorRef.current = null;
    rawStreamRef.current?.getTracks().forEach((t) => t.stop());
    rawStreamRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pendingCandidates.current = [];
    peerIdRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setPeer(null);
    setIncomingOffer(null);
    setStatus("idle");
  }, []);

  const newPeer = useCallback((toId) => {
    const pc = new RTCPeerConnection(ICE);
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "signal", to: toId, signal: { kind: "ice", candidate: e.candidate } });
    };
    pc.ontrack = (e) => setRemoteStream(e.streams[0]);
    pc.onconnectionstatechange = () => {
      if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
        if (status === "active") toast("Call ended");
      }
    };
    pcRef.current = pc;
    return pc;
  }, [send, status]);

  const getMedia = useCallback(async () => {
    const raw = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    rawStreamRef.current = raw;
    let out = raw;
    if (maskModeRef.current !== "off") {
      try {
        const proc = new FaceMaskProcessor();
        processorRef.current = proc;
        out = await proc.start(raw, maskModeRef.current);
      } catch (e) {
        out = raw;
        processorRef.current = null;
      }
    }
    localStreamRef.current = out;
    setLocalStream(out);
    return out;
  }, []);

  const setMaskMode = useCallback(async (mode) => {
    maskModeRef.current = mode;
    setMaskModeState(mode);
    // Live-swap the outgoing video track when a call is already running.
    const pc = pcRef.current;
    const raw = rawStreamRef.current;
    if (!pc || !raw) return;
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
    if (!sender) return;
    try {
      if (mode === "off") {
        processorRef.current?.stop();
        processorRef.current = null;
        const rawVideo = raw.getVideoTracks()[0];
        await sender.replaceTrack(rawVideo);
        const nl = new MediaStream([rawVideo, ...raw.getAudioTracks()]);
        localStreamRef.current = nl;
        setLocalStream(nl);
      } else if (processorRef.current?.running) {
        processorRef.current.setMode(mode);
      } else {
        const proc = new FaceMaskProcessor();
        processorRef.current = proc;
        const out = await proc.start(raw, mode);
        const v = out.getVideoTracks()[0];
        await sender.replaceTrack(v);
        localStreamRef.current = out;
        setLocalStream(out);
      }
    } catch (e) {
      toast.error("Could not switch privacy filter");
    }
  }, []);

  const newGuestPeer = useCallback((toId, stream) => {
    const pc = new RTCPeerConnection(ICE);
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "signal", to: toId, signal: { kind: "ice", candidate: e.candidate } }); };
    pc.ontrack = (e) => setGuests((g) => ({ ...g, [toId]: { ...(g[toId] || {}), stream: e.streams[0] } }));
    stream?.getTracks().forEach((t) => pc.addTrack(t, stream));
    guestPcs.current[toId] = pc;
    return pc;
  }, [send]);

  const removeGuestPeer = useCallback((id) => {
    try { guestPcs.current[id]?.close(); } catch { /* ignore */ }
    delete guestPcs.current[id]; delete guestPending.current[id];
    setGuests((g) => { const n = { ...g }; delete n[id]; return n; });
  }, []);

  // Invitee accepted: connect to BOTH existing participants (mesh) and join as a guest.
  const joinAsGuest = useCallback(async (inv) => {
    try {
      const { data } = await api.post(`/private/invite/${inv.invite_id}/respond`, { accept: true });
      setInvite(null);
      guestRoleRef.current = true;
      roleRef.current = "guest";
      privateRef.current = { session_id: data.session_id, rate: data.rate, begun: true, guest: true, billing: data.billing };
      setPrivateInfo({ session_id: data.session_id, rate: data.rate, minutes: 1, total_charged: data.billing === "split" ? data.rate : 0, status: "active", guest: true, billing: data.billing });
      setPeer(data.creator);
      setStatus("active");
      const stream = await getMedia();
      for (const p of data.participants) {
        const pc = newGuestPeer(p.id, stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        send({ type: "signal", to: p.id, signal: { kind: "join", offer, session_id: data.session_id } });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join the session");
      setInvite(null); cleanup();
    }
  }, [getMedia, newGuestPeer, send, cleanup]);

  const declineInvite = useCallback(async (inv) => {
    setInvite(null);
    try { await api.post(`/private/invite/${inv.invite_id}/respond`, { accept: false }); } catch { /* ignore */ }
  }, []);

  const decideConsent = useCallback(async (req, approve) => {
    setConsent(null);
    try { await api.post(`/private/invite/${req.invite_id}/consent`, { approve }); toast(approve ? `Invite sent to ${req.invitee?.name}` : "Invite refused"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send decision"); }
  }, []);

  const inviteToSession = useCallback(async (address, billing) => {
    const { data } = await api.post("/private/invite", { session_id: privateRef.current?.session_id, address, billing });
    return data;
  }, []);

  const startCall = useCallback(async (targetUser, privateSession = null) => {
    try {
      peerIdRef.current = targetUser.id;
      roleRef.current = "caller";
      answeredRef.current = false;
      if (privateSession) {
        privateRef.current = { ...privateSession };
        setPrivateInfo({ ...privateSession, minutes: 0, total_charged: 0, status: "pending" });
      }
      setPeer(targetUser);
      setStatus("calling");
      const stream = await getMedia();
      const pc = newPeer(targetUser.id);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "signal", to: targetUser.id, signal: { kind: "call", offer, private: privateSession || undefined } });
      timeoutRef.current = setTimeout(() => {
        if (!answeredRef.current) { toast(`${targetUser.display_name} didn't answer`); cleanup(); }
      }, 35000);
    } catch (e) {
      toast.error("Camera/microphone unavailable");
      cleanup();
    }
  }, [getMedia, newPeer, send, cleanup]);

  const startPrivateCall = useCallback(async (targetUser) => {
    try {
      const { data } = await api.post("/private/request", { creator_id: targetUser.id });
      await startCall(targetUser, { session_id: data.session_id, rate: data.rate });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start a private session");
    }
  }, [startCall]);

  const acceptCall = useCallback(async () => {
    stopRingtone();
    answeredRef.current = true;
    try {
      const toId = peerIdRef.current;
      const stream = await getMedia();
      const pc = newPeer(toId);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(incomingOffer));
      for (const c of pendingCandidates.current) await pc.addIceCandidate(new RTCIceCandidate(c));
      pendingCandidates.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "signal", to: toId, signal: { kind: "answer", answer } });
      setStatus("active");
    } catch (e) {
      toast.error("Could not start camera");
      send({ type: "signal", to: peerIdRef.current, signal: { kind: "end" } });
      cleanup();
    }
  }, [getMedia, newPeer, incomingOffer, send, cleanup]);

  const endCall = useCallback((notify = true) => {
    if (guestRoleRef.current) {
      Object.keys(guestPcs.current).forEach((id) => send({ type: "signal", to: id, signal: { kind: "end", guest: true } }));
      if (privateRef.current?.session_id) api.post("/private/leave", { session_id: privateRef.current.session_id }).catch(() => {});
      privateRef.current = null; cleanup(); return;
    }
    if (notify && peerIdRef.current) send({ type: "signal", to: peerIdRef.current, signal: { kind: "end" } });
    Object.keys(guestPcs.current).forEach((id) => send({ type: "signal", to: id, signal: { kind: "end" } }));
    cleanup();
  }, [send, cleanup]);

  useEffect(() => {
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const off = addSignalListener(async (data) => {
      const from = data.from;
      const sig = data.signal || {};
      if (sig.kind === "call") {
        if (pcRef.current) { send({ type: "signal", to: from, signal: { kind: "end" } }); return; }
        peerIdRef.current = from;
        roleRef.current = "callee";
        answeredRef.current = false;
        setIncomingOffer(sig.offer);
        if (sig.private?.session_id) {
          privateRef.current = { ...sig.private };
          setPrivateInfo({ ...sig.private, minutes: 0, total_charged: 0, status: "pending" });
        }
        setStatus("incoming");
        startRingtone();
        try {
          const { data: prof } = await api.get(`/profiles/${from}`);
          setPeer(prof);
          notify("Incoming video date", `${prof.display_name} wants to go live with you`);
        } catch {
          notify("Incoming video date", "Someone wants to go live with you");
        }
      } else if (sig.kind === "join") {
        // A third person is joining our live session — answer their offer on a fresh peer.
        if (!privateRef.current || sig.session_id !== privateRef.current.session_id) return;
        try {
          const pc = newGuestPeer(from, localStreamRef.current);
          await pc.setRemoteDescription(new RTCSessionDescription(sig.offer));
          for (const c of guestPending.current[from] || []) await pc.addIceCandidate(new RTCIceCandidate(c));
          guestPending.current[from] = [];
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "signal", to: from, signal: { kind: "answer", answer, guest: true } });
        } catch { /* ignore */ }
      } else if (sig.kind === "answer" && (guestPcs.current[from] || sig.guest)) {
        const pc = guestPcs.current[from];
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(sig.answer));
          for (const c of guestPending.current[from] || []) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch { /* ignore */ } }
          guestPending.current[from] = [];
        }
      } else if (sig.kind === "ice" && (guestPcs.current[from] || from !== peerIdRef.current)) {
        const pc = guestPcs.current[from];
        if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(new RTCIceCandidate(sig.candidate)); } catch { /* ignore */ } }
        else if (pc || from !== peerIdRef.current) { (guestPending.current[from] = guestPending.current[from] || []).push(sig.candidate); }
      } else if (sig.kind === "end" && from !== peerIdRef.current) {
        if (guestRoleRef.current) { cleanup(); } else { removeGuestPeer(from); }
      } else if (sig.kind === "answer") {
        stopRingtone();
        answeredRef.current = true;
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        if (pcRef.current) {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(sig.answer));
          setStatus("active");
          if (privateRef.current?.session_id && !privateRef.current.begun) {
            privateRef.current.begun = true;
            try {
              const { data: pd } = await api.post("/private/begin", { session_id: privateRef.current.session_id });
              setPrivateInfo((p) => ({ ...(p || {}), ...pd }));
            } catch (err) {
              toast.error(err.response?.data?.detail || "Private session could not start");
              endCall(true);
            }
          }
        }
      } else if (sig.kind === "ice") {
        if (pcRef.current && pcRef.current.remoteDescription) {
          try { await pcRef.current.addIceCandidate(new RTCIceCandidate(sig.candidate)); } catch { /* ignore */ }
        } else {
          pendingCandidates.current.push(sig.candidate);
        }
      } else if (sig.kind === "end") {
        cleanup();
      }
    });
    return off;
  }, [addSignalListener, send, cleanup, endCall, newGuestPeer, removeGuestPeer]);

  useEffect(() => {
    const off = addPrivateListener((evt) => {
      if (evt.type === "private_invite") {
        if (pcRef.current || guestRoleRef.current) return; // already in a call
        setInvite(evt); startRingtone(); setTimeout(stopRingtone, 8000);
        notify("Invite to join a CamChat", `${evt.inviter?.name || "A member"} invited you into a live session`);
        return;
      }
      if (!privateRef.current || evt.session_id !== privateRef.current.session_id) return;
      if (evt.type === "private_invite_consent") {
        setConsent(evt); startRingtone(); setTimeout(stopRingtone, 3000);
        setTimeout(() => setConsent((c) => (c?.invite_id === evt.invite_id ? null : c)), 120000);
        return;
      }
      if (evt.type === "private_guest") {
        if (evt.event === "refused") { toast.error(`${peer?.display_name || "The creator"} refused to add ${evt.name || "that member"}`); return; }
        if (evt.event === "approved") { toast(`${peer?.display_name || "The creator"} approved — invite sent to ${evt.name}`); return; }
        if (evt.event === "joined") { setGuests((g) => ({ ...g, [evt.guest_id]: { ...(g[evt.guest_id] || {}), name: evt.name, photo_url: evt.photo_url, billing: evt.billing } })); toast(`${evt.name} joined the session`); }
        else if (evt.event === "declined") { toast(`${evt.name || "They"} declined the invite`); }
        else if (evt.event === "dropped" || evt.event === "left") {
          if (guestRoleRef.current) { toast(evt.event === "dropped" ? "Your wallet ran out — you've left the session" : "You left the session"); privateRef.current = null; cleanup(); }
          else { const nm = guests[evt.guest_id]?.name || "Your guest"; toast(evt.event === "dropped" ? `${nm} ran out of balance and was dropped` : `${nm} left`); removeGuestPeer(evt.guest_id); }
        }
        return;
      }
      setPrivateInfo((p) => ({ ...(p || {}), session_id: evt.session_id, rate: evt.rate,
        minutes: evt.minutes, total_charged: evt.total_charged, status: evt.status,
        low_balance: !!evt.low_balance }));
      if (evt.status === "ended") {
        privateRef.current = null;
        toast(evt.reason === "balance"
          ? "Wallet balance ran out — private session ended"
          : "Private session ended");
        cleanup();
      }
    });
    return off;
  }, [addPrivateListener, cleanup, removeGuestPeer, guests, peer]);

  return (
    <CallContext.Provider value={{ status, peer, localStream, remoteStream, privateInfo, maskMode, setMaskMode, startCall, startPrivateCall, acceptCall, endCall, guests, invite, joinAsGuest, declineInvite, inviteToSession, consent, decideConsent, isGuest: guestRoleRef.current }}>
      {children}
    </CallContext.Provider>
  );
}

export const useCall = () => useContext(CallContext);
